/**
 * Lead Notes Service
 * Handles API calls for lead notes functionality
 */

import axiosInstance from "../utils/axios";
import { toast } from "sonner";
import { getUserIdFromToken } from "../utils/tokenUtils";

// API Endpoints
const API_ENDPOINTS = {
  LEAD_NOTES: "api/LeadNotes",
};

// ✅ Global cache to prevent redundant fetches
const notesCache = new Map();

export const clearNotesCache = (leadId = null) => {
  if (leadId) {
    const prefix = `${leadId}-`;
    for (const key of notesCache.keys()) {
      if (key === String(leadId) || key.startsWith(prefix)) {
        notesCache.delete(key);
      }
    }
  } else {
    notesCache.clear();
  }
};


/**
 * Get lead notes with filtering and pagination
 * @param {string} leadId - The Lead ID to fetch notes for
 * @param {Object} options - Optional filtering and pagination options
 * @param {boolean} options.isImportant - Filter by important notes (null if not specified)
 * @param {string} options.status - Filter by status (null if not specified)
 * @param {number} options.pageNumber - Page number for pagination (1-based, default: 1)
 * @param {number} options.pageSize - Number of items per page (default: 10, use 0 for all)
 * @param {string} options.sortOrder - Sort order for results (default: "desc")
 * @returns {Promise<Object>} Response object with success, data, message, and totalRecords
 */
export const getLeadNotes = async (leadId, options = {}) => {
  const { forceRefresh = false, ...fetchOptions } = options;

  // ✅ Create a cache key based on leadId and options
  const cacheKey = `${leadId}-${JSON.stringify(fetchOptions)}`;

  // ✅ Check if we have a result or a promise in flight
  if (!forceRefresh && notesCache.has(cacheKey)) {
    const cached = notesCache.get(cacheKey);
    if (cached instanceof Promise) return cached;
    return cached;
  }

  // ✅ Create the fetch promise
  const fetchPromise = (async () => {
    try {
      // Validate required parameter
      if (!leadId) {
        throw new Error("Lead ID is required");
      }

      // Prepare request body for POST request - always send all parameters
      const requestBody = {
        leadId: leadId,
        isImportant: fetchOptions.isImportant !== undefined ? fetchOptions.isImportant : null,
        status: fetchOptions.status || null,
        pageNumber: fetchOptions.pageNumber || 1,
        pageSize: fetchOptions.pageSize || 10,
        sortOrder: fetchOptions.sortOrder || "desc"
      };


      // Make API call using axios instance with POST method
      const response = await axiosInstance.post(
        `${API_ENDPOINTS.LEAD_NOTES}/GetLeadNotes`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const responseData = response.data;

      // Parse the response based on the API structure
      if (responseData.statusCode === 200 && responseData.status === "Success") {
        // Parse the data string if it's a JSON string
        let notesData = [];
        if (typeof responseData.data === "string") {
          try {
            notesData = JSON.parse(responseData.data);
          } catch (parseError) {
            console.error("Error parsing notes data:", parseError);
            notesData = [];
          }
        } else if (Array.isArray(responseData.data)) {
          notesData = responseData.data;
        }

        const result = {
          success: true,
          data: notesData,
          message: responseData.message,
          totalRecords: responseData.totalRecords || notesData.length,
        };

        // ✅ Store data in cache, replacing the promise
        notesCache.set(cacheKey, result);

        return result;
      } else {
        throw new Error(responseData.message || "Failed to fetch lead notes");
      }
    } catch (error) {
      // ✅ Remove failed promise from cache
      notesCache.delete(cacheKey);
      console.error("Error fetching lead notes:", error);

      // Handle different types of errors
      let errorMessage = "Failed to fetch lead notes";

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const serverMessage =
          error.response.data?.message || error.response.data?.error;

        if (status >= 400 && status < 500) {
          // Client errors (4xx)
          errorMessage = serverMessage || `Client error: ${status}`;
        } else if (status >= 500) {
          // Server errors (5xx)
          errorMessage = serverMessage || `Server error: ${status}`;
        }
      } else if (error.request) {
        // Network error - no response received
        errorMessage = "Network error: Unable to connect to server";
      } else {
        // Other errors (validation, parsing, etc.)
        errorMessage = error.message || "Unknown error occurred";
      }

      return {
        success: false,
        error: errorMessage,
        data: [],
        totalRecords: 0,
      };
    }
  })();

  // ✅ Store promise in cache initially
  notesCache.set(cacheKey, fetchPromise);
  return fetchPromise;
};

/**
 * Add a new note to a lead
 * @param {string} leadId - The Lead ID to add note to
 * @param {string} noteDescription - The note content/description
 * @param {boolean} isImportant - Whether the note is marked as important
 * @returns {Promise<Object>} Response object with success status and message
 */
export const addLeadNote = async (leadId, noteDescription, isImportant = false) => {
  try {
    // Validate required parameters
    if (!leadId) {
      throw new Error("Lead ID is required");
    }

    if (!noteDescription || noteDescription.trim() === "") {
      throw new Error("Note description is required");
    }

    // Prepare request body for POST request
    const requestBody = {
      leadId: leadId,
      noteDescription: noteDescription.trim(),
      isImportant: isImportant
    };


    // Make API call using axios instance with POST method
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEAD_NOTES}/AddLeadNotes`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // ✅ Clear cache for this lead so next fetch gets the new note
      clearNotesCache(leadId);

      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Note added successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to add note");
    }
  } catch (error) {
    console.error("Error adding lead note:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to add note";

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Edit an existing lead note
 * @param {string} noteId - The Note ID to edit
 * @param {string} noteDescription - The updated note content/description
 * @param {boolean} isImportant - Whether the note is marked as important
 * @param {string} status - The note status (optional, defaults to "Active")
 * @returns {Promise<Object>} Response object with success status and message
 */
export const editLeadNote = async (noteId, noteDescription, isImportant = false, status = "Active") => {
  try {
    // Validate required parameters
    if (!noteId) {
      throw new Error("Note ID is required");
    }

    if (!noteDescription || noteDescription.trim() === "") {
      throw new Error("Note description is required");
    }

    // Prepare request body for POST request
    const requestBody = {
      noteId: noteId,
      noteDescription: noteDescription.trim(),
      isImportant: isImportant,
      status: status
    };


    // Make API call using axios instance with POST method
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.LEAD_NOTES}/EditLeadNotes`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {


      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Note updated successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to update note");
    }
  } catch (error) {
    console.error("Error editing lead note:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to update note";



    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Get all notes for a lead (convenience function for getting all notes)
 * @param {string} leadId - The Lead ID to fetch all notes for
 * @returns {Promise<Object>} Response object with all notes
 */
export const getAllLeadNotes = async (leadId) => {
  return await getLeadNotes(leadId, {
    pageSize: 0, // Get all notes
    sortOrder: "desc" // Most recent first
  });
};

export default {
  getLeadNotes,
  addLeadNote,
  editLeadNote,
  getAllLeadNotes,
};
